import { useEffect, useState } from "react";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { api } from "@/lib/api";
import { SEOHead } from "@/components/seo/seo-head";
import {
  TrendingUp,
  TrendingDown,
  DollarSign,
  ArrowUpRight,
  ArrowDownRight,
  Calendar,
  Plus,
  Eye,
} from "lucide-react";
import { Skeleton } from "@/components/shared/skeleton";
import { ErrorMessage } from "@/components/shared/error-message";
import { cn } from "@/lib/utils";
import { useCurrency } from "@/hooks/use-currency";
import { useNavigate } from "react-router-dom";
import { useLanguage } from "@/contexts/language-context";
import { WealthMeter, type FinancialHealth } from "@/components/dashboard/wealth-meter";

interface DashboardSummary {
  netWorth: number;
  totalAssets: number;
  totalDebts: number;
  monthlyIncome: number;
  monthlyExpenses: number;
  monthlySavings: number;
  savingsRate: number;
  financialHealth: FinancialHealth;
}

interface RecentTransaction {
  id: string;
  type: "income" | "expense";
  description: string;
  amount: number;
  date: string;
  category?: string;
}

export function DashboardPage() {
  const [summary, setSummary] = useState<DashboardSummary | null>(null);
  const [recentTransactions, setRecentTransactions] = useState<RecentTransaction[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const { formatCurrency } = useCurrency();
  const navigate = useNavigate();
  const { t, language } = useLanguage();

  useEffect(() => {
    fetchDashboardData();
  }, []);

  const fetchDashboardData = async () => {
    try {
      setIsLoading(true);
      const [summaryData, transactionsData] = await Promise.all([
        api.get<{ summary: DashboardSummary }>("/dashboard/summary").catch(() => ({ summary: null })),
        fetchRecentTransactions().catch(() => []),
      ]);

      if (summaryData.summary) {
        setSummary(summaryData.summary);
      }
      setRecentTransactions(transactionsData);
      setError(null);
    } catch (err) {
      setError(err instanceof Error ? err.message : t("common.error"));
    } finally {
      setIsLoading(false);
    }
  };

  const fetchRecentTransactions = async (): Promise<RecentTransaction[]> => {
    try {
      const now = new Date();
      const startOfMonth = new Date(now.getFullYear(), now.getMonth(), 1);

      interface IncomeEntry {
        id: string;
        sourceName: string;
        amount: number;
        date: string;
      }
      interface ExpenseEntry {
        id: string;
        category: string;
        amount: number;
        date: string;
      }

      const [incomes, expenses] = await Promise.all([
        api
          .get<{ incomes: IncomeEntry[] }>(
            `/income?startDate=${startOfMonth.toISOString().slice(0, 10)}&page=1&limit=10`
          )
          .then((res) => res.incomes)
          .catch(() => [] as IncomeEntry[]),
        api
          .get<{ expenses: ExpenseEntry[] }>(
            `/expenses?startDate=${startOfMonth.toISOString().slice(0, 10)}&page=1&limit=10`
          )
          .then((res) => res.expenses)
          .catch(() => [] as ExpenseEntry[]),
      ]);

      const transactions: RecentTransaction[] = [
        ...incomes.map((income) => ({
          id: income.id,
          type: "income" as const,
          description: income.sourceName,
          amount: income.amount,
          date: income.date,
        })),
        ...expenses.map((expense) => ({
          id: expense.id,
          type: "expense" as const,
          description: expense.category,
          amount: expense.amount,
          date: expense.date,
          category: expense.category,
        })),
      ];

      return transactions
        .sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime())
        .slice(0, 8);
    } catch (error) {
      console.error("Failed to fetch recent transactions:", error);
      return [];
    }
  };

  if (isLoading) {
    return (
      <div className="space-y-6">
        <div>
          <Skeleton className="h-9 w-64 mb-2" />
          <Skeleton className="h-5 w-72" />
        </div>
        {/* Hero skeleton */}
        <Card>
          <CardContent className="p-6 sm:p-8">
            <div className="flex flex-col gap-8 lg:flex-row lg:items-center lg:justify-between">
              <div className="space-y-3">
                <Skeleton className="h-4 w-28" />
                <Skeleton className="h-12 w-56 sm:w-72" />
                <Skeleton className="h-4 w-40" />
              </div>
              <div className="w-full lg:max-w-sm space-y-4">
                <Skeleton className="h-4 w-40" />
                <Skeleton className="h-4 w-40" />
                <Skeleton className="h-3 w-full" />
              </div>
            </div>
          </CardContent>
        </Card>
        {/* Stat cards skeleton */}
        <div className="grid gap-4 grid-cols-1 sm:grid-cols-2 lg:grid-cols-3">
          {Array.from({ length: 3 }).map((_, i) => (
            <Card key={i}>
              <CardHeader>
                <Skeleton className="h-4 w-24" />
              </CardHeader>
              <CardContent>
                <Skeleton className="h-8 w-32 mb-2" />
                <Skeleton className="h-4 w-20" />
              </CardContent>
            </Card>
          ))}
        </div>
      </div>
    );
  }

  if (error) {
    return <ErrorMessage message={error} onRetry={fetchDashboardData} />;
  }

  if (!summary) {
    return null;
  }

  const statCards = [
    {
      key: "income",
      label: t("dashboard.monthlyIncome"),
      value: summary.monthlyIncome,
      icon: TrendingUp,
      iconBg: "bg-[#89D7B7]/12",
      iconColor: "text-[#428475] dark:text-[#89D7B7]",
      hoverShadow: "hover:shadow-[#89D7B7]/8",
      footer: (
        <span className="flex items-center gap-1">
          <Calendar className="h-3 w-3" />
          {t("dashboard.thisMonth")}
        </span>
      ),
    },
    {
      key: "expenses",
      label: t("dashboard.monthlyExpenses"),
      value: summary.monthlyExpenses,
      icon: TrendingDown,
      iconBg: "bg-[#C13383]/12",
      iconColor: "text-[#C13383]",
      hoverShadow: "hover:shadow-[#C13383]/8",
      footer: (
        <span className="flex items-center gap-1">
          <Calendar className="h-3 w-3" />
          {t("dashboard.thisMonth")}
        </span>
      ),
    },
    {
      key: "savings",
      label: t("dashboard.savings"),
      value: summary.monthlySavings,
      icon: DollarSign,
      iconBg: "bg-[#792CA2]/12",
      iconColor: "text-[#792CA2]",
      hoverShadow: "hover:shadow-[#792CA2]/8",
      footer:
        summary.savingsRate >= 0 ? (
          <span className="flex items-center gap-1 text-primary">
            <ArrowUpRight className="h-3 w-3" />
            {summary.savingsRate.toFixed(1)}
            {t("dashboard.ofIncome")}
          </span>
        ) : (
          <span className="flex items-center gap-1 text-destructive">
            <ArrowDownRight className="h-3 w-3" />
            {t("dashboard.overspending")}
          </span>
        ),
    },
  ];

  return (
    <>
      <SEOHead
        title="Dashboard - Personal Finance App"
        description="View your financial overview including net worth, monthly income, expenses, savings rate, and financial health status."
        keywords="dashboard, financial overview, net worth, income, expenses, savings"
        canonicalUrl="/"
      />
      <div className="space-y-6 animate-in fade-in duration-700 fill-mode-both">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 animate-in fade-in slide-in-from-left-4 duration-500 fill-mode-both">
          <div className="space-y-1">
            <h1 className="text-2xl sm:text-3xl md:text-4xl font-bold tracking-tight">
              {t("dashboard.title")}
            </h1>
            <p className="text-muted-foreground text-sm sm:text-base">
              {t("dashboard.subtitle")}
            </p>
          </div>
          <div className="flex gap-2">
            <Button variant="outline" size="sm" onClick={() => navigate("/budget")}>
              <Plus className="h-4 w-4 mr-2" />
              {t("dashboard.addTransaction")}
            </Button>
            <Button variant="outline" size="sm" onClick={() => navigate("/reports")}>
              <Eye className="h-4 w-4 mr-2" />
              {t("dashboard.viewReports")}
            </Button>
          </div>
        </div>

        {/* Hero — Wealth Meter */}
        <WealthMeter
          netWorth={summary.netWorth}
          totalAssets={summary.totalAssets}
          totalDebts={summary.totalDebts}
          savingsRate={summary.savingsRate}
          financialHealth={summary.financialHealth}
        />

        {/* Stat cards — varied accent per card, staggered entrance */}
        <div className="grid gap-4 grid-cols-1 sm:grid-cols-2 lg:grid-cols-3">
          {statCards.map((card, idx) => (
            <Card
              key={card.key}
              className={cn(
                "hover:-translate-y-0.5 hover:shadow-lg transition-all duration-200 border border-border/60 animate-in fade-in slide-in-from-bottom-3 duration-500 fill-mode-both",
                card.hoverShadow
              )}
              style={{ animationDelay: `${150 + idx * 100}ms` }}
            >
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                <CardTitle className="text-sm font-medium text-muted-foreground">
                  {card.label}
                </CardTitle>
                <div className={cn("h-10 w-10 rounded-full flex items-center justify-center", card.iconBg)}>
                  <card.icon className={cn("h-5 w-5", card.iconColor)} />
                </div>
              </CardHeader>
              <CardContent>
                <div
                  className={cn(
                    "text-2xl font-bold font-display tracking-tight",
                    card.key === "savings" &&
                      (summary.monthlySavings >= 0 ? "text-primary" : "text-destructive")
                  )}
                >
                  {formatCurrency(card.value)}
                </div>
                <p className="text-xs text-muted-foreground mt-2 flex items-center gap-1">
                  {card.footer}
                </p>
              </CardContent>
            </Card>
          ))}
        </div>

        {/* Recent Transactions */}
        <Card className="hover:shadow-lg hover:shadow-black/5 dark:hover:shadow-black/30 transition-all duration-200 animate-in fade-in slide-in-from-bottom-3 duration-500 fill-mode-both" style={{ animationDelay: "500ms" }}>
          <CardHeader className="flex flex-row items-center justify-between">
            <div>
              <CardTitle className="text-lg">{t("dashboard.recentTransactions")}</CardTitle>
              <CardDescription>{t("dashboard.latestIncomeAndExpenses")}</CardDescription>
            </div>
            <Button variant="ghost" size="sm" onClick={() => navigate("/budget")}>
              {t("dashboard.viewAll")}
            </Button>
          </CardHeader>
          <CardContent>
            {recentTransactions.length > 0 ? (
              <div className="space-y-3">
                {recentTransactions.slice(0, 6).map((transaction) => (
                  <div
                    key={transaction.id}
                    className="flex items-center justify-between p-3 rounded-lg border hover:bg-muted/50 transition-colors"
                  >
                    <div className="flex items-center gap-3 flex-1 min-w-0">
                      <div
                        className={cn(
                          "h-10 w-10 rounded-full flex items-center justify-center shrink-0",
                          transaction.type === "income"
                            ? "bg-[#89D7B7]/10"
                            : "bg-[#C13383]/10"
                        )}
                      >
                        {transaction.type === "income" ? (
                          <ArrowUpRight className="h-5 w-5 text-[#428475] dark:text-[#89D7B7]" />
                        ) : (
                          <ArrowDownRight className="h-5 w-5 text-[#C13383]" />
                        )}
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="font-medium text-sm truncate">{transaction.description}</p>
                        <p className="text-xs text-muted-foreground">
                          {new Date(transaction.date).toLocaleDateString(
                            language === "id" ? "id-ID" : "en-US",
                            { month: "short", day: "numeric" }
                          )}
                        </p>
                      </div>
                    </div>
                    <div
                      className={cn(
                        "font-semibold text-sm",
                        transaction.type === "income" ? "text-[#428475] dark:text-[#89D7B7]" : "text-[#C13383]"
                      )}
                    >
                      {transaction.type === "income" ? "+" : "-"}
                      {formatCurrency(Math.abs(transaction.amount))}
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-8 text-muted-foreground">
                <p className="text-sm">{t("dashboard.noTransactions")}</p>
                <Button
                  variant="outline"
                  size="sm"
                  className="mt-4"
                  onClick={() => navigate("/budget")}
                >
                  <Plus className="h-4 w-4 mr-2" />
                  {t("dashboard.addTransaction")}
                </Button>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </>
  );
}
